import { FunnelIcon } from '@heroicons/react/24/outline';
import type { AiChatColumnMeta } from '@plato/app-meta/ai-meta';
import { SalesMetricBasis } from '@plato/app-meta/sales-metric';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import {
    MetricHintExplanation,
    useMetricHintOpenedTracking,
} from '@plato/app-shared/components/metric-explainer';
import { DataText } from '@plato/ui-legacy/components/typography/data-text';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { Button } from '@plato/ui-legacy/components/ui/button';
import type { ColumnDef } from '@plato/ui/components/data-table';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from '@plato/ui/components/info-hint';
import { SparkAreaChart, SparkBarChart } from '@tremor/react';
import { getYear, subYears } from 'date-fns';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CellObject } from 'xlsx';
import type { ExplorerGetExplorerTotalSummary } from '../../../server/types/infer-trpc-types.ts';
import type {
    GrossMarginPercentageYearPeriodKey,
    SparklineMonth,
} from '../../../server/utils/explorer-utils.ts';
import {
    ExplorerSortBy,
    getDateRange,
    TargetUnit,
    validSorts,
    VisibilityWindow,
    yearPeriodsHelper,
} from '../../../server/utils/explorer-utils.ts';
import { useAcl } from '../../acl/use-acl.ts';
import { DeltaDoubleDeckerCell } from '../../list-views/components/delta-double-decker-cell.tsx';
import { useExplorerView } from './use-explorer-query-params.ts';

export type XlsxCellValue = string | number | boolean | Date | CellObject | null;

export type ExplorerTableData = {
    id?: string | null;
    name?: string | null;
    revenue_current: number;
    revenue_last_period: number;
    gross_margin_current: number;
    gross_margin_last_period: number;
    gross_margin_percentage_current: number;
    gross_margin_percentage_last_period: number;
    revenue_delta_amount: number;
    revenue_delta_percentage: number | null;
    gross_margin_delta_amount: number;
    gross_margin_delta_percentage: number | null;
    units_current: number;
    units_last_period: number;
    units_delta_amount: number;
    units_delta_percentage: number | null;
    monthly_sales_over_last_2_years: SparklineMonth[];
} & Record<GrossMarginPercentageYearPeriodKey, number>;

export type ConfigurableColumnDefinition<T extends ExplorerTableData> = {
    columns: {
        id: string;
        accessorKey: string;
        headerText: string;
        meta?: { aiChat?: AiChatColumnMeta };
        generateCell: (value: T) => React.ReactNode;
        size?: number;
    }[];
};

type ExplorerColumnDef<T extends ExplorerTableData> = ColumnDef<
    T,
    ExplorerGetExplorerTotalSummary,
    ExplorerSortBy
> & {
    id: string;
    exporter?: {
        name: string;
        getXlsxCell: (data: T) => XlsxCellValue;
    }[];
};

function isExplorerSortBy(value: string): value is ExplorerSortBy {
    const sorts: readonly string[] = validSorts;
    return sorts.includes(value);
}

const SPARK_CHART_COLORS = ['tremor-chart-primary'];
const SPARK_AREA_REVENUE_CATEGORIES = ['monthly_sales'];
const SPARK_AREA_UNITS_CATEGORIES = ['monthly_unit_total'];
const SPARK_BAR_ORDER_COUNT_CATEGORIES = ['monthly_order_count'];

// APP-1756: These chart components cannot be inlined in the 'useMemo' present in the columns definition.
// Every time the memo recomputes, the inline arrow functions are recreated with fresh references.
// This causes the cell components to unmount and remount rather than re-render in place, which result in a "flicker" effect.
function RevenueSparkAreaCell({ row }: { row: { original: ExplorerTableData } }): ReactNode {
    return (
        <SparkAreaChart
            autoMinValue
            data={row.original.monthly_sales_over_last_2_years}
            index="month_year"
            categories={SPARK_AREA_REVENUE_CATEGORIES}
            colors={SPARK_CHART_COLORS}
            className="mx-auto"
            curveType="monotone"
        />
    );
}

function OrderCountSparkBarCell({ row }: { row: { original: ExplorerTableData } }): ReactNode {
    return (
        <SparkBarChart
            data={row.original.monthly_sales_over_last_2_years}
            index="month_year"
            categories={SPARK_BAR_ORDER_COUNT_CATEGORIES}
            colors={SPARK_CHART_COLORS}
            className="mx-auto border-b border-neutral-200"
        />
    );
}

function UnitsSparkAreaCell({ row }: { row: { original: ExplorerTableData } }): ReactNode {
    return (
        <SparkAreaChart
            autoMinValue
            data={row.original.monthly_sales_over_last_2_years}
            index="month_year"
            categories={SPARK_AREA_UNITS_CATEGORIES}
            colors={SPARK_CHART_COLORS}
            className="mx-auto"
            curveType="monotone"
        />
    );
}

export type ExplorerColumnsContext = Pick<
    ReturnType<typeof useExplorerView>['state'],
    'window' | 'yearPeriods' | 'targetUnit' | 'customDateRange' | 'billing'
>;

export function useConfigurableExplorerColumns<T extends ExplorerTableData = ExplorerTableData>(
    config: ConfigurableColumnDefinition<T>,
) {
    const { state } = useExplorerView();
    return useExplorerColumnsWithContext(config, state);
}

export function useExplorerColumnsWithContext<T extends ExplorerTableData = ExplorerTableData>(
    config: ConfigurableColumnDefinition<T>,
    state: ExplorerColumnsContext,
) {
    const { t } = useTranslation('explorer');
    const trackMetricHintOpened = useMetricHintOpenedTracking();
    const acl = useAcl();

    const { window, yearPeriods, targetUnit, customDateRange, billing } = state;

    const dateRange = useMemo(
        () =>
            getDateRange(
                window,
                customDateRange
                    ? { from: new Date(customDateRange.from), to: new Date(customDateRange.to) }
                    : null,
            ) ?? {
                from: new Date(),
                to: new Date(),
            },
        [window, customDateRange],
    );

    const columns: ExplorerColumnDef<T>[] = useMemo(() => {
        // The monetary columns show the same data for every billing status; only the labels differ.
        const getWindowLabel = (yearsAgo = 0): string => {
            const to = subYears(dateRange.to, yearsAgo);
            const options = { from: subYears(dateRange.from, yearsAgo), to, period: getYear(to) } as const;
            switch (window) {
                case VisibilityWindow.CUSTOM_DATES:
                    return t('table_label__period_CUSTOM_DATES', { ns: 'explorer', ...options });
                case VisibilityWindow.FULL_YEAR:
                    return t('table_label__period_FULL_YEAR', { ns: 'explorer', ...options });
                case VisibilityWindow.LAST_MONTH:
                    return t('table_label__period_LAST_MONTH', { ns: 'explorer', ...options });
                case VisibilityWindow.LAST_QUARTER:
                    return t('table_label__period_LAST_QUARTER', { ns: 'explorer', ...options });
                case VisibilityWindow.LAST_WEEK:
                    return t('table_label__period_LAST_WEEK', { ns: 'explorer', ...options });
                case VisibilityWindow.TODAY:
                    return t('table_label__period_TODAY', { ns: 'explorer', ...options });
                case VisibilityWindow.TTM:
                    return t('table_label__period_TTM', { ns: 'explorer', ...options });
                case VisibilityWindow.YESTERDAY:
                    return t('table_label__period_YESTERDAY', { ns: 'explorer', ...options });
                case VisibilityWindow.YTD:
                    return t('table_label__period_YTD', { ns: 'explorer', ...options });
            }
        };

        const getPeriodLabel = (period: string): string => {
            switch (billing) {
                case SalesMetricBasis.BILLED:
                    return t('table_label__revenue_period', { period });
                case SalesMetricBasis.BILLED_WO_CREDIT_NOTE:
                    return t('table_label__billed_wo_credit_note_period', { period });
                case SalesMetricBasis.NOT_BILLED:
                    return t('table_label__unbilled_period', { period });
                case SalesMetricBasis.ORDER_SUM:
                    return t('table_label__order_sum_period', { period });
            }
        };

        const getSparkChartLabel = (): string => {
            switch (billing) {
                case SalesMetricBasis.BILLED:
                    return t('table_label__revenue_spark_chart');
                case SalesMetricBasis.BILLED_WO_CREDIT_NOTE:
                    return t('table_label__billed_wo_credit_note_spark_chart');
                case SalesMetricBasis.NOT_BILLED:
                    return t('table_label__unbilled_spark_chart');
                case SalesMetricBasis.ORDER_SUM:
                    return t('table_label__order_sum_spark_chart');
            }
        };

        const tempCols: ExplorerColumnDef<T>[] = [
            {
                id: 'select',
                cell: ({ row }) =>
                    row.original.id ? (
                        <Button
                            variant="outline"
                            onClick={() => {
                                row.toggleSelected(!row.getIsSelected());
                            }}
                            selected={row.getIsSelected()}
                        >
                            <FunnelIcon className="h-3 w-3" />
                            {t(
                                row.getIsSelected()
                                    ? 'filter_header__delete_button'
                                    : 'filter_header__filter_button',
                                {
                                    ns: 'generic-list',
                                },
                            )}
                        </Button>
                    ) : null,
                enableSorting: false,
                size: 120,
                meta: {
                    preserveOpacityWhenDimmed: true,
                },
            },
        ];

        tempCols.push(
            ...(config.columns.map((customColumn) => ({
                id: customColumn.id,
                accessorKey: customColumn.accessorKey,
                size: customColumn.size,
                cell: ({ row }) => customColumn.generateCell(row.original),
                header: customColumn.headerText,
                meta: {
                    aiChat: customColumn.meta?.aiChat,
                    sortBy: isExplorerSortBy(customColumn.id) ? customColumn.id : undefined,
                    summary: () => (
                        <div className="flex items-center gap-1">
                            {t('total_summary__name')}
                            <InfoHint>
                                <InfoHintTrigger />
                                <InfoHintContent side="right">
                                    <InfoHintHeader>
                                        <InfoHintTitle>{t('total_summary__name')}</InfoHintTitle>
                                    </InfoHintHeader>
                                    <InfoHintBody>{t('total_summary__name_tooltip')}</InfoHintBody>
                                </InfoHintContent>
                            </InfoHint>
                        </div>
                    ),
                },
            })) as ExplorerColumnDef<T>[]),
        );

        if (targetUnit === TargetUnit.REVENUE) {
            tempCols.push(
                {
                    id: 'revenue_current',
                    accessorKey: 'revenue_current',
                    header: getPeriodLabel(getWindowLabel()),
                    cell: ({ cell }) => (
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{cell.getValue() as string}</CurrencyOrEmpty>
                        </DataText>
                    ),
                    meta: {
                        sortBy: ExplorerSortBy.REVENUE_CURRENT,
                        align: 'right',
                        aiChat: {
                            label: getPeriodLabel(getWindowLabel()),
                        },
                        summary: (data) => (
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>{data.revenue_current}</CurrencyOrEmpty>
                            </DataText>
                        ),
                        hint: <MetricHintExplanation metric={billing} />,
                        onHintOpen: () => {
                            trackMetricHintOpened(billing);
                        },
                    },
                    exporter: [
                        {
                            name: getPeriodLabel(getWindowLabel()),
                            getXlsxCell: (row: ExplorerTableData) => row.revenue_current,
                        },
                    ],
                },
                {
                    id: 'revenue_last_period',
                    accessorKey: 'revenue_last_period',
                    header: getPeriodLabel(getWindowLabel(1)),
                    cell: ({ cell }) => (
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{cell.getValue() as string}</CurrencyOrEmpty>
                        </DataText>
                    ),
                    meta: {
                        sortBy: ExplorerSortBy.REVENUE_LAST_PERIOD,
                        align: 'right',
                        aiChat: {
                            label: getPeriodLabel(getWindowLabel(1)),
                        },
                        summary: (data) => (
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>{data.revenue_last_period}</CurrencyOrEmpty>
                            </DataText>
                        ),
                        hint: <MetricHintExplanation metric={billing} />,
                        onHintOpen: () => {
                            trackMetricHintOpened(billing);
                        },
                    },
                    exporter: [
                        {
                            name: getPeriodLabel(getWindowLabel(1)),
                            getXlsxCell: (row: ExplorerTableData) => row.revenue_last_period,
                        },
                    ],
                },
                {
                    id: 'revenue_delta_amount',
                    accessorKey: 'revenue_delta_amount',
                    header: t('table_label__revenue_delta_amount', {
                        ns: 'explorer',
                    }),
                    cell: ({ row }) => {
                        return (
                            <DeltaDoubleDeckerCell
                                percentage={row.original.revenue_delta_percentage}
                                amount={row.original.revenue_delta_amount}
                            />
                        );
                    },
                    meta: {
                        aiChat: { label: t('table_label__revenue_delta_amount', { ns: 'explorer' }) },
                        sortBy: ExplorerSortBy.REVENUE_DELTA_AMOUNT,
                        align: 'right',
                        summary: (data) => (
                            <DeltaDoubleDeckerCell
                                percentage={data.revenue_delta_percentage}
                                amount={data.revenue_delta_amount}
                            />
                        ),
                    },
                    exporter: [
                        {
                            name: t('table_label__revenue_delta_amount', {
                                defaultValue: 'revenue_delta_amount',
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.revenue_delta_amount,
                        },
                        {
                            name: t('table_label__revenue_delta_percentage', {
                                defaultValue: 'revenue_delta_percentage',
                            }),
                            getXlsxCell: (row: ExplorerTableData) =>
                                row.revenue_delta_percentage === null
                                    ? null
                                    : { v: row.revenue_delta_percentage, t: 'n', z: '0.00%' },
                        },
                    ],
                },
            );

            // Revenue yearPeriods
            const newReneueYearPeriods: ExplorerColumnDef<T>[] = yearPeriodsHelper
                .getIntervals(yearPeriods)
                .map((interval) => {
                    const id = yearPeriodsHelper.getRevenueColumnNameByInterval(interval);

                    return {
                        id,
                        accessorKey: id,
                        header: getPeriodLabel(getWindowLabel(interval)),
                        cell: ({ cell }) => (
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>{cell.getValue() as string}</CurrencyOrEmpty>
                            </DataText>
                        ),
                        meta: {
                            sortBy: id,
                            align: 'right',
                            summary: (data) => (
                                <DataText>
                                    <CurrencyOrEmpty showEmptyIfZero>
                                        {data[id as keyof ExplorerGetExplorerTotalSummary]}
                                    </CurrencyOrEmpty>
                                </DataText>
                            ),
                            hint: <MetricHintExplanation metric={billing} />,
                            onHintOpen: () => {
                                trackMetricHintOpened(billing);
                            },
                        },
                        exporter: [
                            {
                                name: getPeriodLabel(getWindowLabel(interval)),
                                getXlsxCell: (row: ExplorerTableData) =>
                                    row[id as keyof ExplorerTableData] as number,
                            },
                        ],
                    };
                });

            tempCols.push(...newReneueYearPeriods);

            tempCols.push(
                {
                    id: 'revenue_ttm_monthly_sales_over_last_2_years',
                    accessorKey: 'monthly_sales_over_last_2_years',
                    header: getSparkChartLabel(),
                    cell: RevenueSparkAreaCell,
                },
                {
                    id: 'revenue_ttm_monthly_orders_over_last_2_years',
                    accessorKey: 'monthly_sales_over_last_2_years',
                    header: t('table_label__order_count_spark_chart', {
                        ns: 'explorer',
                    }),
                    cell: OrderCountSparkBarCell,
                },
                // ----------------- Margin -----------------
                {
                    id: 'gross_margin_current',
                    accessorKey: 'gross_margin_current',
                    header: () => (
                        <MaskedContent>
                            {t('table_label__margin_period', {
                                period: getWindowLabel(),
                            })}
                        </MaskedContent>
                    ),
                    cell: ({ cell }) => (
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>{cell.getValue() as string}</CurrencyOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    meta: {
                        sortBy: ExplorerSortBy.GROSS_MARGIN_CURRENT,
                        aiChat: {
                            label: t('table_label__gross_margin_current', { ns: 'explorer' }),
                            requires: ['canReadGrossMargin'],
                        },
                        align: 'right',
                        summary: (data) => (
                            <MaskedContent>
                                <DataText>
                                    <CurrencyOrEmpty showEmptyIfZero>
                                        {data.gross_margin_current}
                                    </CurrencyOrEmpty>
                                </DataText>
                            </MaskedContent>
                        ),
                        hint: <MetricHintExplanation base={billing} metric="GROSS_MARGIN" />,
                        onHintOpen: () => {
                            trackMetricHintOpened('GROSS_MARGIN');
                        },
                    },
                    exporter: [
                        {
                            name: t('table_label__margin_period', {
                                period: getWindowLabel(),
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.gross_margin_current,
                        },
                    ],
                },
                {
                    id: 'gross_margin_percentage_current',
                    accessorKey: 'gross_margin_percentage_current',
                    header: () => (
                        <MaskedContent>
                            {t('table_label__margin_pct_period', {
                                period: getWindowLabel(),
                            })}
                        </MaskedContent>
                    ),
                    cell: ({ cell }) => (
                        <MaskedContent>
                            <DataText>
                                <PercentageOrEmpty showEmptyIfZero>
                                    {cell.getValue() as number}
                                </PercentageOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    meta: {
                        sortBy: ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_CURRENT,
                        aiChat: {
                            label: t('table_label__gross_margin_percentage_current', { ns: 'explorer' }),
                            requires: ['canReadGrossMargin'],
                        },
                        align: 'right',
                        summary: (data) => (
                            <MaskedContent>
                                <DataText>
                                    <PercentageOrEmpty showEmptyIfZero>
                                        {data.gross_margin_percentage_current}
                                    </PercentageOrEmpty>
                                </DataText>
                            </MaskedContent>
                        ),
                        hint: <MetricHintExplanation base={billing} metric="GROSS_MARGIN_PERCENTAGE" />,
                        onHintOpen: () => {
                            trackMetricHintOpened('GROSS_MARGIN_PERCENTAGE');
                        },
                    },
                    exporter: [
                        {
                            name: t('table_label__margin_pct_period', {
                                period: getWindowLabel(),
                            }),
                            getXlsxCell: (row: ExplorerTableData) => ({
                                v: row.gross_margin_percentage_current,
                                t: 'n',
                                z: '0.00%',
                            }),
                        },
                    ],
                },
                {
                    id: 'gross_margin_last_period',
                    accessorKey: 'gross_margin_last_period',
                    header: () => (
                        <MaskedContent>
                            {t('table_label__margin_period', {
                                period: getWindowLabel(1),
                            })}
                        </MaskedContent>
                    ),
                    cell: ({ cell }) => (
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>{cell.getValue() as string}</CurrencyOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    meta: {
                        sortBy: ExplorerSortBy.GROSS_MARGIN_LAST_PERIOD,
                        aiChat: {
                            label: t('table_label__gross_margin_last_period', { ns: 'explorer' }),
                            requires: ['canReadGrossMargin'],
                        },
                        align: 'right',
                        summary: (data) => (
                            <MaskedContent>
                                <DataText>
                                    <CurrencyOrEmpty showEmptyIfZero>
                                        {data.gross_margin_last_period}
                                    </CurrencyOrEmpty>
                                </DataText>
                            </MaskedContent>
                        ),
                        hint: <MetricHintExplanation base={billing} metric="GROSS_MARGIN" />,
                        onHintOpen: () => {
                            trackMetricHintOpened('GROSS_MARGIN');
                        },
                    },
                    exporter: [
                        {
                            name: t('table_label__margin_period', {
                                period: getWindowLabel(1),
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.gross_margin_last_period,
                        },
                    ],
                },
                {
                    id: 'gross_margin_percentage_last_period',
                    accessorKey: 'gross_margin_percentage_last_period',
                    header: () => (
                        <MaskedContent>
                            {t('table_label__margin_pct_period', {
                                period: getWindowLabel(1),
                            })}
                        </MaskedContent>
                    ),
                    cell: ({ cell }) => (
                        <MaskedContent>
                            <DataText>
                                <PercentageOrEmpty showEmptyIfZero>
                                    {cell.getValue() as number}
                                </PercentageOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    meta: {
                        sortBy: ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_LAST_PERIOD,
                        aiChat: {
                            label: t('table_label__gross_margin_percentage_last_period', { ns: 'explorer' }),
                            requires: ['canReadGrossMargin'],
                        },
                        align: 'right',
                        summary: (data) => (
                            <MaskedContent>
                                <DataText>
                                    <PercentageOrEmpty showEmptyIfZero>
                                        {data.gross_margin_percentage_last_period}
                                    </PercentageOrEmpty>
                                </DataText>
                            </MaskedContent>
                        ),
                        hint: <MetricHintExplanation base={billing} metric="GROSS_MARGIN_PERCENTAGE" />,
                        onHintOpen: () => {
                            trackMetricHintOpened('GROSS_MARGIN_PERCENTAGE');
                        },
                    },
                    exporter: [
                        {
                            name: t('table_label__margin_pct_period', {
                                period: getWindowLabel(1),
                            }),
                            getXlsxCell: (row: ExplorerTableData) => ({
                                v: row.gross_margin_percentage_last_period,
                                t: 'n',
                                z: '0.00%',
                            }),
                        },
                    ],
                },
                {
                    id: 'gross_margin_delta_amount',
                    accessorKey: 'gross_margin_delta_amount',
                    header: () => (
                        <MaskedContent>
                            {t('table_label__gross_margin_delta_amount', {
                                ns: 'explorer',
                            })}
                        </MaskedContent>
                    ),
                    cell: ({ row }) => {
                        return (
                            <DeltaDoubleDeckerCell
                                percentage={row.original.gross_margin_delta_percentage}
                                amount={row.original.gross_margin_delta_amount}
                                masked={true}
                            />
                        );
                    },
                    meta: {
                        sortBy: ExplorerSortBy.GROSS_MARGIN_DELTA_AMOUNT,
                        aiChat: {
                            label: t('table_label__gross_margin_delta_amount', { ns: 'explorer' }),
                            requires: ['canReadGrossMargin'],
                        },
                        align: 'right',
                        summary: (data) => (
                            <DeltaDoubleDeckerCell
                                percentage={data.gross_margin_delta_percentage}
                                amount={data.gross_margin_delta_amount}
                                masked
                            />
                        ),
                    },
                    exporter: [
                        {
                            name: t('table_label__gross_margin_delta_amount', {
                                defaultValue: 'gross_margin_delta_amount',
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.gross_margin_delta_amount,
                        },
                        {
                            name: t('table_label__gross_margin_delta_percentage', {
                                defaultValue: 'gross_margin_delta_percentage',
                            }),
                            getXlsxCell: (row: ExplorerTableData) =>
                                row.gross_margin_delta_percentage === null
                                    ? null
                                    : { v: row.gross_margin_delta_percentage, t: 'n', z: '0.00%' },
                        },
                    ],
                },
            );

            // Margin yearPeriods with GM % next to each GM column
            const newMarginYearPeriods: ExplorerColumnDef<T>[] = yearPeriodsHelper
                .getIntervals(yearPeriods)
                .flatMap((interval) => {
                    const marginId = yearPeriodsHelper.getGrossMarginColumnNameByInterval(interval);
                    const marginPctId =
                        yearPeriodsHelper.getGrossMarginPercentageColumnNameByInterval(interval);

                    const marginColumn: ExplorerColumnDef<T> = {
                        id: marginId,
                        accessorKey: marginId,
                        header: () => (
                            <MaskedContent>
                                {t('table_label__margin_period', {
                                    period: getWindowLabel(interval),
                                })}
                            </MaskedContent>
                        ),
                        cell: ({ cell }) => (
                            <MaskedContent>
                                <DataText>
                                    <CurrencyOrEmpty showEmptyIfZero>
                                        {cell.getValue() as string}
                                    </CurrencyOrEmpty>
                                </DataText>
                            </MaskedContent>
                        ),
                        meta: {
                            sortBy: marginId,
                            align: 'right',
                            summary: (data) => (
                                <MaskedContent>
                                    <DataText>
                                        <CurrencyOrEmpty showEmptyIfZero>
                                            {data[marginId as keyof ExplorerGetExplorerTotalSummary]}
                                        </CurrencyOrEmpty>
                                    </DataText>
                                </MaskedContent>
                            ),
                            hint: <MetricHintExplanation base={billing} metric="GROSS_MARGIN" />,
                            onHintOpen: () => {
                                trackMetricHintOpened('GROSS_MARGIN');
                            },
                        },
                        exporter: [
                            {
                                name: t('table_label__margin_period', {
                                    period: getWindowLabel(interval),
                                }),
                                getXlsxCell: (row: ExplorerTableData) =>
                                    row[marginId as keyof ExplorerTableData] as number,
                            },
                        ],
                    };

                    const marginPctColumn: ExplorerColumnDef<T> = {
                        id: marginPctId,
                        accessorKey: marginPctId,
                        header: () => (
                            <MaskedContent>
                                {t('table_label__margin_pct_period', {
                                    period: getWindowLabel(interval),
                                })}
                            </MaskedContent>
                        ),
                        cell: ({ cell }) => (
                            <MaskedContent>
                                <DataText>
                                    <PercentageOrEmpty showEmptyIfZero>
                                        {cell.getValue() as number}
                                    </PercentageOrEmpty>
                                </DataText>
                            </MaskedContent>
                        ),
                        meta: {
                            sortBy: marginPctId,
                            align: 'right',
                            summary: (data) => (
                                <MaskedContent>
                                    <DataText>
                                        <PercentageOrEmpty showEmptyIfZero>
                                            {data[marginPctId as keyof ExplorerGetExplorerTotalSummary]}
                                        </PercentageOrEmpty>
                                    </DataText>
                                </MaskedContent>
                            ),
                            hint: <MetricHintExplanation base={billing} metric="GROSS_MARGIN_PERCENTAGE" />,
                            onHintOpen: () => {
                                trackMetricHintOpened('GROSS_MARGIN_PERCENTAGE');
                            },
                        },
                        exporter: [
                            {
                                name: t('table_label__margin_pct_period', {
                                    period: getWindowLabel(interval),
                                }),
                                getXlsxCell: (row: ExplorerTableData) => ({
                                    v: row[marginPctId],
                                    t: 'n',
                                    z: '0.00%',
                                }),
                            },
                        ],
                    };

                    return [marginColumn, marginPctColumn];
                });

            tempCols.push(...newMarginYearPeriods);

            // --- end
        } else {
            tempCols.push(
                {
                    id: 'units_current',
                    accessorKey: 'units_current',
                    header: t('table_label__units_current', {
                        ns: 'explorer',
                    }),
                    cell: ({ cell }) => (
                        <DataText>
                            <NumberOrEmpty showEmptyIfZero>{cell.getValue() as string}</NumberOrEmpty>
                        </DataText>
                    ),
                    meta: {
                        aiChat: { label: t('table_label__units_current', { ns: 'explorer' }) },
                        sortBy: ExplorerSortBy.UNITS_CURRENT,
                        align: 'right',
                        summary: (data) => (
                            <DataText>
                                <NumberOrEmpty showEmptyIfZero>{data.units_current}</NumberOrEmpty>
                            </DataText>
                        ),
                        hint: <MetricHintExplanation metric="UNITS" />,
                        onHintOpen: () => {
                            trackMetricHintOpened('UNITS');
                        },
                    },
                    exporter: [
                        {
                            name: t('table_label__units_current', { defaultValue: 'units_current' }),
                            getXlsxCell: (row: ExplorerTableData) => row.units_current,
                        },
                    ],
                },
                {
                    id: 'units_last_period',
                    accessorKey: 'units_last_period',
                    header: t('table_label__units_last_period', {
                        ns: 'explorer',
                    }),
                    cell: ({ cell }) => (
                        <DataText>
                            <NumberOrEmpty showEmptyIfZero>{cell.getValue() as string}</NumberOrEmpty>
                        </DataText>
                    ),
                    meta: {
                        aiChat: { label: t('table_label__units_last_period', { ns: 'explorer' }) },
                        sortBy: ExplorerSortBy.UNITS_LAST_PERIOD,
                        align: 'right',
                        summary: (data) => (
                            <DataText>
                                <NumberOrEmpty showEmptyIfZero>{data.units_last_period}</NumberOrEmpty>
                            </DataText>
                        ),
                        hint: <MetricHintExplanation metric="UNITS" />,
                        onHintOpen: () => {
                            trackMetricHintOpened('UNITS');
                        },
                    },
                    exporter: [
                        {
                            name: t('table_label__units_last_period', {
                                defaultValue: 'units_last_period',
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.units_last_period,
                        },
                    ],
                },
                {
                    id: 'units_delta_amount',
                    accessorKey: 'units_delta_amount',
                    header: t('table_label__units_delta_amount', {
                        ns: 'explorer',
                    }),
                    cell: ({ row }) => {
                        return (
                            <DeltaDoubleDeckerCell
                                percentage={row.original.units_delta_percentage}
                                amount={row.original.units_delta_amount}
                                style="number"
                            />
                        );
                    },
                    meta: {
                        aiChat: { label: t('table_label__units_delta_amount', { ns: 'explorer' }) },
                        sortBy: ExplorerSortBy.UNITS_DELTA_AMOUNT,
                        align: 'right',
                        summary: (data) => (
                            <DeltaDoubleDeckerCell
                                percentage={data.units_delta_percentage}
                                amount={data.units_delta_amount}
                                style="number"
                            />
                        ),
                    },
                    exporter: [
                        {
                            name: t('table_label__units_delta_amount', {
                                defaultValue: 'units_delta_amount',
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.units_delta_amount,
                        },
                        {
                            name: t('table_label__units_delta_percentage', {
                                defaultValue: 'units_delta_percentage',
                            }),
                            getXlsxCell: (row: ExplorerTableData) =>
                                row.units_delta_percentage === null
                                    ? null
                                    : { v: row.units_delta_percentage, t: 'n', z: '0.00%' },
                        },
                    ],
                },
                {
                    id: 'units_ttm_monthly_sales_over_last_2_years',
                    accessorKey: 'units_ttm_monthly_sales_over_last_2_years',
                    header: t('table_label__units_spark_chart', {
                        ns: 'explorer',
                    }),
                    cell: UnitsSparkAreaCell,
                },
                {
                    id: 'units_ttm_monthly_count_over_last_2_years',
                    accessorKey: 'monthly_sales_over_last_2_years',
                    header: t('table_label__order_count_spark_chart', {
                        ns: 'explorer',
                    }),
                    cell: OrderCountSparkBarCell,
                },
            );
        }

        return tempCols.filter((col) => acl.can('read', 'SalesReport', col.id as never));
    }, [
        //
        acl,
        billing,
        config.columns,
        t,
        targetUnit,
        window,
        yearPeriods,
        dateRange.from,
        dateRange.to,
        trackMetricHintOpened,
    ]);

    return columns;
}
